import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
  Box,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { Chess } from "chess.js";
import { Chessboard } from "react-chessboard";

import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import Swal from "sweetalert2";
import { QRCodeSVG } from "qrcode.react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next"; // Added import for translations
import GameEndInfo from "./GameEndInfo";
import { useScreenshot, createFileName } from "use-react-screenshot";
import { useParams } from "react-router-dom";
import MoveLog from "./MoveLog";
import Clock from "./Clock"; // Import Clock component
import { useWebSocketManager} from "../services/useWebSocketManager";
import {
    sendMessage,
    addListener,
    removeListener,
  } from "../services/websocketManager";
import "./ResponsiveChessPage.css";

import EvaluationBar from "./EvaluationBar";
import PaymentConfirmationDialog from "./PaymentConfirmationDialog.js";

const ResponsiveChessPage = ({
  generalMessage,
  updateBackground,
  viewer,
  userData,
  setShowAppBar,
}) => {
  const { invitationId, invitePlayerColor } = useParams(); // Extract invitationId and playerColor
  const [,] = useState(invitePlayerColor); // Use playerColor from URL
  const { t } = useTranslation(); // Initialize translations
  const [checkedKingSquare, setCheckedKingSquare] = useState(null);
  const navigate = useNavigate();
  const [game] = useState(new Chess());
  const [historyGame] = useState(new Chess());    // For PGN and SAN only
  const [pgn, setPgn] = useState(null);
  const [fen, setFen] = useState(game.fen());
  const [playerColor, setPlayerColor] = useState("white");
  const [gameId, setGameId] = useState(null);
  const [, setViewerFromServer] = useState(false);
  const [, setShowBlink] = useState(false);
  const [movesGuesses, setMovesGuesses] = useState(null);
  const [totalMoves, setTotalMoves] = useState(0);
  const [totalGuesses, setTotalGuesses] = useState(0);
  const [playerTime, setPlayerTime] = useState(0);
  const [opponentTime, setOpponentTime] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [bestMove, setBestMove] = useState(null);
  const [bestFen, setBestFen] = useState(null);
  const [capturedWhite, setCapturedWhite] = useState([]);
  const [capturedBlack, setCapturedBlack] = useState([]);
  const [generalMessageTeamStatus, setGeneralMessageTeamStatus] =
    useState(null);
  const stockfishRef = useRef(null);
  const [, setGeneralMessageInit] = useState(null);
  const [aiLevel, setAiLevel] = useState(-1);
  const [aiMoveTimeout, setAiMoveTimeout] = useState(true);
  const [gameInfo, setGameInfo] = useState(null);
  const [showGameEnd, setShowGameEnd] = useState(false);
  const lastAIMoveRef = useRef(null);
  const location = useLocation();
  const generalMessageViaState = location.state?.generalMessage;
  const desktopScreen = "500";
  const mobileScreen = "350";
  const { gameData } = useWebSocketManager();
  const [, setUserDataFromServer] = useState(false);
  const [showViewerDialog, setShowViewerDialog] = useState(false); // State for the dialog
  const [isViewer, setIsViewer] = useState(false); // Track if the user chose to be a viewer
  const [openOfferDrawDialog, setOpenOfferDrawDialog] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [dontShowEmojiAgain, setDontShowEmojiAgain] = useState(false);
  const [offerDrawMessage, setOfferDrawMessage] = useState("");
  const [isAIExecuting, setIsAIExecuting] = useState(false);
  const ref = useRef(null);
  const [, takeScreenShot] = useScreenshot({
    type: "image/jpeg",
    quality: 1.0,
  });
  const [openEmojiDialog, setOpenEmojiDialog] = useState(false);
  const [emojiMessage, setEmojiMessage] = useState("");
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);
  const [showGameHistory, setShowGameHistory] = useState(false);
  const [showScreenshotExtras, setShowScreenshotExtras] = useState(false);
  const scrollRef = useRef(null);
  const isMobile = window.innerWidth <= 600; // Check if the screen size is mobile
  const handleScrollUp = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };
  const [evaluation, setEvaluation] = useState(null); // Add state to track
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const customSquareStyles = checkedKingSquare
    ? {
        [checkedKingSquare]: {
          backgroundColor: "rgba(255, 0, 0, 0.5)", // semi-transparent red
          borderRadius: "50%",
        },
      }
    : {};
  const aiLevelRef = useRef(aiLevel);
  const [doRedirectToMain, setDoRedirectToMain] = useState(true);
  const [isCoinsDialogOpen, setIsCoinsDialogOpen] = useState(false);
   useEffect(() => {
      
      const handleWebSocketEvent = (event) => {
        if (
          event.type === "message" &&
          event.message.messageType === "pgn"
        ) {
            let pgn = historyGame.pgn();
            const generalMessage = { ...generalMessageTemplate };
            generalMessage.messageType = "pgn";
            generalMessage.gameInfo.pgn = pgn;
            generalMessage.gameInfo.gameId = generalMessageViaState?.game.gameId;
            sendMessage(generalMessage);
            setPgn(pgn);
        }
      };
      // Subscribe to WebSocket events
      addListener(handleWebSocketEvent);
  
      // Cleanup on component unmount
      return () => {
        removeListener(handleWebSocketEvent);
      };
    }, [generalMessageViaState?.game.gameId, historyGame]);

  useEffect(() => {
    aiLevelRef.current = aiLevel;
  }, [aiLevel]);

   useEffect(() => {
    const handlePopState = () => {
      console.log("Back button pressed");
      const leaveMessage = { ...generalMessageTemplate };
          leaveMessage.messageType = "leave_game";
          leaveMessage.game.gameId = gameId;
          leaveMessage.color = playerColor;
          sendMessage(leaveMessage);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [gameId, playerColor]);

  const analyzePosition = useCallback(
    (fen) => {
      if (stockfishRef.current) {
        const levelToSet = aiLevelRef.current > 0 ? aiLevelRef.current : 12;
        stockfishRef.current.postMessage(`position fen ${fen}`);
        stockfishRef.current.postMessage("go depth " + levelToSet);
      }
    },
    [] // ✅ Now safe to keep empty
  );

 const handleSquareClick = (square) => {
  if (
    (playerColor === "white" && game.turn() !== "w") ||
    (playerColor === "black" && game.turn() !== "b") ||
    gameOver
  ) {
    return;
  }

  // If clicking the already selected square, deselect it
  if (selectedSquare === square) {
    setSelectedSquare(null);
    setLegalMoves([]);
    return;
  }

  // If no selection yet, and the piece is the player's
  if (!selectedSquare) {
    const moves = game.moves({ square, verbose: true });
    if (moves.length === 0) return;

    const legalDestinations = moves.map((m) => m.to);
    setSelectedSquare(square);
    setLegalMoves(legalDestinations);
    return;
  }

  // Attempt to move
  const moveResult = handleMove(selectedSquare, square);
  if (moveResult) {
    setSelectedSquare(null);
    setLegalMoves([]);
  } else {
    // clicked a different piece — restart selection
    const moves = game.moves({ square, verbose: true });
    if (moves.length > 0) {
      const legalDestinations = moves.map((m) => m.to);
      setSelectedSquare(square);
      setLegalMoves(legalDestinations);
    } else {
      setSelectedSquare(null);
      setLegalMoves([]);
    }
  }
};

const highlightStyles = legalMoves.reduce((acc, sq) => {
  const piece = game.get(sq);
  const isCapture = piece && piece.color !== game.turn();

  acc[sq] = {
    backgroundColor: isCapture
      ? "rgba(255, 0, 0, 0.4)" // red for captures
      : "rgba(255, 255, 0, 0.3)", // yellow for regular moves
    borderRadius: "50%",
  };
  return acc;
}, {});

// Add selected square styling
const selectedSquareStyle = selectedSquare ? {
  [selectedSquare]: {
    backgroundColor: "rgba(0, 255, 0, 0.5)", // green for selected piece
    borderRadius: "50%",
  }
} : {};

const combinedSquareStyles = {
  ...customSquareStyles,
  ...highlightStyles,
  ...selectedSquareStyle, // Selected square takes priority
};

  function handleGameStart(generalMessage) {
    if (!generalMessage) return;

    const shouldDelay =
      generalMessageViaState?.playAgainstAITeam === true ||
      generalMessageViaState?.game?.aiAsHuman === true;

    if (generalMessage.messageType === "init_game") {
      localStorage.removeItem("dontShowOfferDrawDialog");
      localStorage.removeItem("dontShowEmoji");

      setGeneralMessageInit(generalMessage);
      setPlayerColor(generalMessage.color);

      const gameId = generalMessage?.game?.gameId;
      if (gameId) {
        localStorage.setItem("lastGameId", gameId);
        setGameId(gameId);
      }

      setAiLevel(generalMessage.gameInfo.aiLevel);
      setAiMoveTimeout(generalMessage.game.aiAsHuman);
      const initialFen = generalMessage.game.initialFen || game.fen();
      const fullFen = `${initialFen}`;
      game.load(fullFen);
      setFen(game.fen());

      if (game.inCheck()) {
        const colorInCheck = game.turn();
        const kingSquare = getKingSquare(
          colorInCheck === "w" ? "white" : "black"
        );
        setCheckedKingSquare(kingSquare);
      } else {
        setCheckedKingSquare(null);
      }

      analyzePosition(game.fen());
    }

    if (
      generalMessage.messageType === "server_move_hint" &&
      generalMessage.move.commandFen
    ) {
      executeAIMove(generalMessage.move.commandMove, true);
    }

    if (
      generalMessage.messageType === "server_move" &&
      generalMessage.move.commandFen
    ) {
      const actualFromToArray = [
        generalMessage.move.commandMove.slice(0, 2),
        generalMessage.move.commandMove.slice(2),
      ];

      const move = {
        from: actualFromToArray[0],
        to: actualFromToArray[1],
        color: generalMessage.move.color,
        promotion: "q",
      };

      game.load(generalMessage.move.commandFen);

      if (shouldDelay === false) {
        analyzePosition(game.fen());
        setFen(game.fen());
      }

      if (game.inCheck()) {
        const colorInCheck = game.turn();
        const kingSquare = getKingSquare(
          colorInCheck === "w" ? "white" : "black"
        );
        setCheckedKingSquare(kingSquare);
      } else {
        setCheckedKingSquare(null);
      }

      updateCapturedPieces();

      analyzePosition(game.fen());
      //console.log("Move done");

      addToGameHistory(game.fen(), move.from + move.to, generalMessage.move.color);

      // const newHistory = [
      //   ...getMoveHistory().slice(0, getCurrentMoveIndex() + 1),
      //   {
      //     fen: game.fen(),
      //     move: move.from + "-" + move.to,
      //     color: move.color,
      //   },
      // ];
      // updateMoveHistory(newHistory);
      // setCurrentMoveIndex(newHistory.length - 1);
    }

    if (generalMessage.messageType === "team_member_move") {
      setGeneralMessageTeamStatus(generalMessage);
    }

    if (generalMessage.messageType === "game_fen") {
      setFen(generalMessage.move.commandFen);
    }

    if (generalMessage.messageType === "game_over") {
      setGameInfo(generalMessage.gameInfo);
      setGameOver(true);
      setShowGameEnd(true);
      handleScrollUp();
      setShowBlink(true);
    }

    if (generalMessage?.messageType === "update_time") {
      const lastGameId = localStorage.getItem("lastGameId");
      if (generalMessage.game?.gameId === lastGameId) {
        setGeneralMessageTeamStatus(generalMessage);

        if (playerColor === generalMessage.color) {
          setPlayerTime(generalMessage.move.currentSeconds);
          setOpponentTime(generalMessage.move.opponentSeconds);
        } else {
          setPlayerTime(generalMessage.move.opponentSeconds);
          setOpponentTime(generalMessage.move.currentSeconds);
        }

        if (
          generalMessage.move.totalGuesses > 0 &&
          generalMessage.move.totalMoves > 0
        ) {
          setTotalMoves(generalMessage.move.totalMoves);
          setTotalGuesses();
          const mg = generalMessage.move.totalGuesses;
          setMovesGuesses(mg);
        }
      } else {
        console.error("skipped time");
      }
    }

    if (generalMessage.messageType === "offer_draw_request") {
      const dontShow = localStorage.getItem("dontShowOfferDrawDialog");
      if (!dontShow) {
        setOfferDrawMessage(t("Would you like to accept the draw offer?"));
        setOpenOfferDrawDialog(true);
      }
    }

    if (generalMessage.messageType === "offer_draw_reject") {
      Swal.fire({
        title: t("The draw offer was rejected"),
        icon: "error",
        draggable: true,
        confirmButtonText: t("OK"),
      });
    }

    if (generalMessage.messageType === "emoji") {
      const dontShowEmoji = localStorage.getItem("dontShowEmoji");
      if (!dontShowEmoji) {
        setEmojiMessage(generalMessage.content);
        setOpenEmojiDialog(true);
      }
    }
  }

  useEffect(() => {
    if (gameOver) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }, [gameOver]);

  useEffect(() => {
    if (gameData) {
      //console.log(gameData);
    }
  }, [gameData]);

  useEffect(() => {
    if (generalMessageViaState) {
      if (generalMessageViaState.messageType === "init_game") {
        handleGameStart(generalMessageViaState);
        setGeneralMessageTeamStatus(generalMessageViaState);
      } else if (generalMessageViaState.messageType === "continue_game") {
        setGameId(generalMessageViaState.gameInfo.gameId);

        const initialFen = generalMessageViaState.game.currentFen;
        const fullFen = `${initialFen}`;
        game.load(fullFen);
        setFen(game.fen());
        if (game.inCheck()) {
          const colorInCheck = game.turn(); // "w" or "b"
          const kingSquare = getKingSquare(
            colorInCheck === "w" ? "white" : "black"
          );
          setCheckedKingSquare(kingSquare);
        } else {
          setCheckedKingSquare(null);
        }
        analyzePosition(game.fen());
        setGeneralMessageTeamStatus(generalMessageViaState);
        setPlayerColor(generalMessageViaState.gameInfo.color);
        localStorage.removeItem("uncompleted_game", null);
      }
    }
    // eslint-disable-next-line no-use-before-define
  }, [analyzePosition, generalMessageViaState]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault(); // refreshing or click back the page
      event.returnValue = ""; // Required for Chrome, shows a warning message
      const generalMessage = { ...generalMessageTemplate };
      generalMessage.messageType = "exitGame";
      generalMessage.game.gameId = generalMessageViaState?.game.gameId;
      generalMessage.gameInfo.gameId = generalMessageViaState?.game.gameId;
      sendMessage(generalMessage);
    };

    window.addEventListener("beforeunload", handleBeforeUnload); // entering the page

    return () => {
      if(doRedirectToMain === true){
        const generalMessage = { ...generalMessageTemplate };
        generalMessage.messageType = "leave_game";
        generalMessage.game.gameId = generalMessageViaState?.game.gameId;
        generalMessage.gameInfo.gameId = generalMessageViaState?.game.gameId;
        sendMessage(generalMessage);
        window.removeEventListener("beforeunload", handleBeforeUnload);
        //navigate("/main");
      }
      
    };
  }, [doRedirectToMain, generalMessageViaState?.game.gameId]);

  useEffect(() => {
    window.addEventListener("popstate", handleBackNavigation);

    return () => {
      window.removeEventListener("popstate", handleBackNavigation);
    };
  }, []);

  const handleBackNavigation = () => {
    if (doRedirectToMain === true){
      const generalMessage = { ...generalMessageTemplate };
      generalMessage.messageType = "leave_game";
      generalMessage.game.gameId = generalMessageViaState?.game?.gameId;
      generalMessage.gameInfo.gameId = generalMessageViaState?.game?.gameId;
      sendMessage(generalMessage);

      navigate("/main");
    }
    
  };

  useEffect(() => {
    if (gameOver === false) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto"; // Enable scrolling on unmount
    };
  }, [isMobile, gameOver]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0; // Scroll to top once the component is mounted
    }
  }, []);

  useEffect(() => {
    // Show the viewer dialog if the viewer prop is true
    if (viewer) {
      setShowViewerDialog(true);
    }
  }, [viewer]);

  useEffect(() => {
    setUserDataFromServer((prev) => userData || prev);
  }, [userData]);

  const getMoveHistory = useCallback(
    () =>
      JSON.parse(localStorage.getItem("moveHistory")) || [
        { fen: game.fen(), move: null },
      ],
    [game]
  );
  const updateMoveHistory = (history) => {
  const lastMovePerColorPerTurn = new Map();
  let moveNumber = 0;

  history.forEach((entry) => {
    if (entry.color === "white") {
      moveNumber += 1;
    }

    // Key = move number + color ("3_white" / "3_black")
    const key = `${moveNumber}_${entry.color}`;
    lastMovePerColorPerTurn.set(key, { ...entry, moveNumber });
  });

  // Reconstruct in correct order
  const deduplicated = Array.from(lastMovePerColorPerTurn.values())
    .sort((a, b) => a.moveNumber - b.moveNumber || (a.color === "white" ? -1 : 1))
    .map(({ moveNumber, ...entry }) => entry); // remove moveNumber before saving

  localStorage.setItem("moveHistory", JSON.stringify(deduplicated));
};

  const getCurrentMoveIndex = () =>
    parseInt(localStorage.getItem("currentMoveIndex"), 10) || 0;
  const setCurrentMoveIndex = (index) =>
    localStorage.setItem("currentMoveIndex", index);

  const updateCapturedPieces = useCallback(() => {
    const initialCounts = {
      w: { p: 8, r: 2, n: 2, b: 2, q: 1, k: 1 },
      b: { p: 8, r: 2, n: 2, b: 2, q: 1, k: 1 },
    };

    const currentCounts = {
      w: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 },
      b: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 },
    };

    // Count pieces currently on the board
    game
      .board()
      .flat()
      .forEach((square) => {
        if (square) {
          currentCounts[square.color][square.type]++;
        }
      });

    const newWhiteCaptured = [];
    const newBlackCaptured = [];

    // Importance mapping for sorting pieces (lower is more important)
    const pieceImportance = { k: 1, q: 2, r: 3, b: 4, n: 5, p: 6 };

    // Calculate captured pieces
    Object.keys(initialCounts.w).forEach((piece) => {
      const missingWhitePieces =
        initialCounts.w[piece] - currentCounts.w[piece];
      const missingBlackPieces =
        initialCounts.b[piece] - currentCounts.b[piece];

      if (missingWhitePieces > 0)
        newBlackCaptured.push(...Array(missingWhitePieces).fill(piece));
      if (missingBlackPieces > 0)
        newWhiteCaptured.push(...Array(missingBlackPieces).fill(piece));
    });

    // Sort captured pieces based on their importance
    const sortedWhiteCaptured = newWhiteCaptured.sort(
      (a, b) => pieceImportance[a] - pieceImportance[b]
    );
    const sortedBlackCaptured = newBlackCaptured.sort(
      (a, b) => pieceImportance[a] - pieceImportance[b]
    );

    // Update state with sorted captured pieces
    setCapturedWhite(sortedWhiteCaptured);
    setCapturedBlack(sortedBlackCaptured);
  }, [game]);

  useEffect(() => {
    if (localStorage.getItem("moveHistory")) {
      updateMoveHistory([{ fen: game.fen(), move: null }]);
    }
    if (localStorage.getItem("currentMoveIndex")) {
      setCurrentMoveIndex(0);
    }

    const history = getMoveHistory();
    const index = getCurrentMoveIndex();
    setFen(history[index].fen);
    if (game.inCheck()) {
      const colorInCheck = game.turn(); // "w" or "b"
      const kingSquare = getKingSquare(
        colorInCheck === "w" ? "white" : "black"
      );
      setCheckedKingSquare(kingSquare);
    } else {
      setCheckedKingSquare(null);
    }
    game.load(history[index].fen);
    updateCapturedPieces();
  }, [game, getMoveHistory, updateCapturedPieces]);

  const handleOfferDrawClose = () => {
    setOpenOfferDrawDialog(false);
  };

  const handleOfferDrawConfirm = () => {
    // Logic to accept the draw offer
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "offer_draw_accept";
    generalMessage.game.gameId = generalMessageViaState?.game.gameId;
    generalMessage.gameInfo.gameId = generalMessageViaState?.game.gameId;
    sendMessage(generalMessage);
    setOpenOfferDrawDialog(false);
  };

  const handleOfferDrawReject = () => {
    // Logic to reject the draw offer
    const generalMessage = { ...generalMessageTemplate };
    generalMessage.messageType = "offer_draw_reject";
    generalMessage.game.gameId = generalMessageViaState?.game.gameId;
    generalMessage.gameInfo.gameId = generalMessageViaState?.game.gameId;
    sendMessage(generalMessage);
    setOpenOfferDrawDialog(false);
  };

  const handleDontShowAgainChange = (event) => {
    setDontShowAgain(event.target.checked);
    if (event.target.checked) {
      localStorage.setItem("dontShowOfferDrawDialog", "true");
    } else {
      localStorage.removeItem("dontShowOfferDrawDialog");
    }
  };

  const handleDontShowEmojiAgainChange = (event) => {
    setDontShowEmojiAgain(event.target.checked);
    if (event.target.checked) {
      localStorage.setItem("dontShowEmoji", "true");
    } else {
      localStorage.removeItem("dontShowEmoji");
    }
  };

  const handleEmojiDialogClose = () => {
    setOpenEmojiDialog(false);
  };

  const handleJoinAsViewer = () => {
    setIsViewer(true); // Mark the user as a viewer
    setShowViewerDialog(false); // Close the dialog
    const inviteMessage = { ...generalMessageTemplate };
    inviteMessage.messageType = "invited_viewer";
    inviteMessage.gameInfo.gameId = invitationId;
    inviteMessage.color = invitePlayerColor;

    sendMessage(inviteMessage); // Notify the server
  };

  const handleDeclineViewer = () => {
    setIsViewer(false);
    setShowViewerDialog(false);
    navigate("/main"); // Navigate away if the user declines
  };

 
  function getFenAfterMove(game, move) {
    if (!game || typeof game.fen !== "function") {
      console.warn("Invalid game object passed to getFenAfterMove");
      return null;
    }

    try {
      const tempGame = new Chess(game.fen()); // clone the game
      const from = move?.substring(0, 2);
      const to = move?.substring(2, 4);

      const result = tempGame.move({ from, to, promotion: "q" }); // Add promotion if needed
      return result ? tempGame.fen() : null;
    } catch (error) {
      console.error("Failed to get FEN after move:", error);
      return null;
    }
  }

  useEffect(() => {
    updateBackground({
      image:
        "https://res.cloudinary.com/dnlgtek2a/image/upload/v1752358330/Pictures/Scroll_1.jpg",
    });

    const isLocalhost = window.location.hostname === "localhost";

    stockfishRef.current = new Worker(
      isLocalhost
        ? `${process.env.PUBLIC_URL}/stockfish.wasm.js`
        : `${process.env.PUBLIC_URL}/stockfish.wasm.js` //"https://lichess1.org/stockfish/stockfish.wasm.js"
    );

    stockfishRef.current.postMessage("uci"); // ✅ INIT
    stockfishRef.current.postMessage("isready"); // ✅ Wait for readiness

    stockfishRef.current.onmessage = (event) => {
      const message = event.data;

      if (message.startsWith("info depth") && message.includes("score")) {
        const scoreMatch = message.match(/score (cp|mate) (-?\d+)/);
        if (scoreMatch) {
          const type = scoreMatch[1];
          const value = parseInt(scoreMatch[2], 10);

          // ✅ Only update evaluation if it's currently the player's turn
          const isMyTurn =
            (playerColor === "white" && game.turn() === "w") ||
            (playerColor === "black" && game.turn() === "b");

          if (isMyTurn) {
            setEvaluation({ type, value });
          }
        }
      }

      if (message.startsWith("bestmove")) {
        const move = message.split(" ")[1];
        //const fenAtBestMove = game.fen(); // capture current board after move analysis

        if (!move || lastAIMoveRef.current === move) {
          return;
        }

        setBestMove(move);
        setBestFen(getFenAfterMove(game, move));
        lastAIMoveRef.current = move; // ✅ Set here to prevent replay

        // ✅ ONLY execute AI move if it's AI's turn and game is synced
        if (
          //generalMessageViaState?.playAgainstAITeam === true &&
          game.turn() !== playerColor[0]
          // && !lastAIMoveRef.current
        ) {
          if (
            generalMessageViaState?.playAgainstAITeam === true ||
            generalMessageViaState?.playAgainstAI === true
          ) {
            executeAIMove(move); // ✅ Now the move matches current board
          }
        }
      }
    };

    // Trigger AI's move if AI starts as white
    if (generalMessageViaState?.playAgainstAI && playerColor === "black") {
      analyzePosition(game.fen());
    }

    return () => {
      updateBackground({
        image:
          "https://res.cloudinary.com/dnlgtek2a/image/upload/v1743415495/Pictures/cyume5jns2oovggj8z4g.png",
        size: "cover",
        position: "center",
        repeat: "no-repeat",
        attachment: "fixed", // Ensures background stays fixed during scrolling
      });

      if (stockfishRef.current) {
        stockfishRef.current.terminate();
      }
    };
  }, [updateBackground, generalMessageViaState, playerColor, analyzePosition]);

  useEffect(() => {
    if (invitationId) {
      setGameId(invitationId);
      setPlayerColor(invitePlayerColor);
    }
  }, [invitationId, gameId, invitePlayerColor]);

  useEffect(() => {
    if (viewer === true) {
      setViewerFromServer(viewer);
    }
  }, [viewer]);

  useEffect(() => {
    handleGameStart(generalMessage);
  }, [generalMessage]);

  const handleMove = (sourceSquare, targetSquare) => {
    if (invitationId) {
      return;
    }
    if (gameOver === true) {
      return;
    }
    if (
      (playerColor === "white" && game.turn() !== "w") ||
      (playerColor === "black" && game.turn() !== "b")
    ) {
      console.warn("It's not your turn!");
      return false;
    }

    let move;
    try {
      move = game.move({
        from: sourceSquare,
        to: targetSquare,
        promotion: "q",
      });
      if (!move) return false;
    } catch (error) {
      return false;
    }

    //updateHistory(move);

    setFen(game.fen());

    if (game.inCheck()) {
      const colorInCheck = game.turn(); // "w" or "b"
      const kingSquare = getKingSquare(
        colorInCheck === "w" ? "white" : "black"
      );
      setCheckedKingSquare(kingSquare);
    } else {
      setCheckedKingSquare(null);
    }

    updateCapturedPieces();

    if (generalMessageViaState?.playAgainstAITeam === false) {
      analyzePosition(game.fen());
    }

    let lastGameId = localStorage.getItem("lastGameId");
    let gameStatus = checkGameOver();
    const generalMessageToSend = { ...generalMessageTemplate };
    generalMessageToSend.move.userMove = sourceSquare + targetSquare;
    generalMessageToSend.move.bestMove = bestMove;
    generalMessageToSend.move.bestFen = bestFen;
    generalMessageToSend.move.userFen = game.fen();

    generalMessageToSend.game.gameId = lastGameId;
    generalMessageToSend.move.color = playerColor;
    generalMessageToSend.messageType = "user_move";
    generalMessageToSend.move.aiMove = false;
    generalMessageToSend.color = playerColor;
    generalMessageToSend.game.gameStatus = gameStatus;
    generalMessageToSend.move.gameStatus = gameStatus;
    generalMessageToSend.user.userType = "Human";

    // Send position to Stockfish for analysis
    //analyzePosition(game.fen()); - to analyze the opponent best move

    sendMessage(generalMessageToSend);

    setSelectedSquare(null);
    setLegalMoves([]);

    return true;
  };

  const handleHintClick = () => {
    if (bestMove) {
      setAiLevel(15);
      //executeAIMove(bestMove); // Reuse your existing function
      const sourceSquare = bestMove?.substring(0, 2);
      const targetSquare = bestMove?.substring(2, 4);
      let lastGameId = localStorage.getItem("lastGameId");
      const generalMessageToSend = {
        ...generalMessageTemplate,
        move: {
          userMove: sourceSquare + targetSquare,
          bestMove: bestMove,
          bestFen: bestFen,
          userFen: game.fen(),
          color: playerColor,
          gameStatus: checkGameOver(),
          aiMove: false,
        },
        game: { gameId: lastGameId, gameStatus: checkGameOver() },

        messageType: "user_move_hint",

        color: playerColor,
        user: { userType: "Human" },
      };

      sendMessage(generalMessageToSend);
      setIsAIExecuting(false);
    } else {
      Swal.fire({
        title: t("Hint not available"),
        text: t("Please wait a few seconds for the analysis"),
        icon: "warning",
        confirmButtonText: t("OK"),
      });
    }
  };

  const getRandomInt = (min, max) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  const executeAIMove = (bestMove, hint) => {
    if (gameOver || !bestMove || isAIExecuting) {
      console.warn("Game is over or AI is already executing a move.");
      return;
    }
    const shouldDelay =
      generalMessageViaState?.playAgainstAITeam === true ||
      generalMessageViaState?.game?.aiAsHuman === true;

    setIsAIExecuting(true); // Lock further execution
    const lastGameId = localStorage.getItem("lastGameId");

    let randomDelay = aiMoveTimeout
      ? Math.floor(Math.random() * (10000 - 5000 + 1)) + 5000
      : 1000;

    if (generalMessageViaState?.playAgainstAI === true) {
      randomDelay = 2000;
    }
    
    if (shouldDelay === true) {
      randomDelay = getRandomInt(5000, 10000); // Random delay between 5 and 10 seconds
    }

    if (hint === true) {
      randomDelay = 0;
      addToGameHistory(game.fen(), bestMove?.substring(0, 2) + bestMove?.substring(2, 4), playerColor);
      //console.log("Move done");
      // const newHistory = [
      //   ...getMoveHistory().slice(0, getCurrentMoveIndex() + 1),
      //   {
      //     fen: game.fen(),
      //     move: bestMove?.substring(0, 2) + "-" + bestMove?.substring(2, 4),
      //     color: playerColor === "white" ? "white" : "black",
      //   },
      // ];
      // updateMoveHistory(newHistory);
      // setCurrentMoveIndex(newHistory.length - 1);
    }
    else{
      // if (generalMessageViaState?.playAgainstAI === true) {
        
      //   console.log("Move done");
      //   const newHistory = [
      //     ...getMoveHistory().slice(0, getCurrentMoveIndex() + 1),
      //     {
      //       fen: game.fen(),
      //       move: bestMove?.substring(0, 2) + "-" + bestMove?.substring(2, 4),
      //       color: playerColor === "white" ? "black" : "white",
      //     },
      //   ];
      //   updateMoveHistory(newHistory);
      //   setCurrentMoveIndex(newHistory.length - 1);
      //   }
    }

    setTimeout(() => {
      const sourceSquare = bestMove?.substring(0, 2);
      const targetSquare = bestMove?.substring(2, 4);

      // Prevent replay of last move
      if (lastAIMoveRef.current === sourceSquare + targetSquare) {
        //setIsAIExecuting(false);
        //return;
      }

      let move;
      try {
        move = game.move({
          from: sourceSquare,
          to: targetSquare,
          promotion: "q",
        });
        if (!move) {
          setIsAIExecuting(false);
          return;
        }
      } catch (error) {
        console.error("Invalid move:", error);
        setIsAIExecuting(false);
        return;
      }

      lastAIMoveRef.current = sourceSquare + targetSquare;
      setFen(game.fen());
      updateCapturedPieces();
      analyzePosition(game.fen());

      const generalMessageToSend = {
        ...generalMessageTemplate,
        move: {
          userMove: sourceSquare + targetSquare,
          bestMove: bestMove,
          bestFen: bestFen,
          userFen: game.fen(),
          color: playerColor === "white" ? "black" : "white",
          gameStatus: checkGameOver(),
          aiMove: true,
        },
        game: { gameId: lastGameId, gameStatus: checkGameOver() },

        messageType: "user_move",

        color: playerColor === "white" ? "black" : "white",
        user: { userType: "AI" },
      };

      if (!hint) {
        sendMessage(generalMessageToSend);
      }
      if (generalMessageViaState?.playAgainstAI === true && !hint) {
        
        addToGameHistory(game.fen(), bestMove, playerColor === "white" ? "black" : "white");
        //console.log("Move done");
        // const newHistory = [
        //   ...getMoveHistory().slice(0, getCurrentMoveIndex() + 1),
        //   {
        //     fen: game.fen(),
        //     move: bestMove?.substring(0, 2) + "-" + bestMove?.substring(2, 4),
        //     color: playerColor === "white" ? "black" : "white",
        //   },
        // ];
        // updateMoveHistory(newHistory);
        // setCurrentMoveIndex(newHistory.length - 1);
        }

      setIsAIExecuting(false);
    }, randomDelay);

    
  };

  function onAnalytics(){
    setIsCoinsDialogOpen(true);
    
  }

  const getKingSquare = (color) => {
    const board = game.board();
    for (let rank = 0; rank < 8; rank++) {
      for (let file = 0; file < 8; file++) {
        const piece = board[rank][file];
        if (piece && piece.type === "k" && piece.color === color[0]) {
          const fileLetter = "abcdefgh"[file];
          const rankNumber = 8 - rank;
          return `${fileLetter}${rankNumber}`;
        }
      }
    }
    return null;
  };

  // Add this function inside your component, after `const [game] = useState(new Chess());`
const addToGameHistory = (fen, moveUci, color) => {
  try {

     const moveObj = {
      from: moveUci.substring(0, 2),
      to: moveUci.substring(2, 4),
      promotion: moveUci.length > 4 ? moveUci[4] : undefined,
    };

    const result = historyGame.move(moveObj);
    if (!result) {
      console.error("Invalid move for historyGame", moveObj, historyGame.fen());
      return;
    }
    
    // Get SAN for the move that was just played
    // moveUci should be like "e2e4"
    const history = getMoveHistory().slice(0, getCurrentMoveIndex() + 1);
  
    // To get SAN, use game.history({ verbose: true }) and look at the last move.
     
    // Compose new entry
    const newEntry = {
      fen,
      move: moveUci.substring(0, 2) + "-" + moveUci.substring(2, 4), // e.g., "e2-e4"
      san: historyGame.history({ verbose: true }).slice(-1)[0].san, // Get SAN of the last move
      color,
      
    };
  
    // Add to history and update
    const newHistory = [...history, newEntry];
    updateMoveHistory(newHistory);
    setCurrentMoveIndex(newHistory.length - 1);
  
    // Also update historyGame object (never set FEN, just add the move in UCI or SAN)
    // Use UCI → {from, to}
   
    //historyGame.move(moveObj);
    //console.log("Current:", historyGame.pgn());
  } catch (error) {
    console.error("Error adding to game history:", error);
  }
};


  const checkGameOver = () => {
    //const history = game.history();
    //console.log(history);
    let ret = "in_progress";
    if (game.isCheckmate()) {
      const winner = game.turn() === "w" ? "Black" : "White";
      if (winner === "White") {
        ret = "white_won_by_mat_black";
        //console.log("White won by checkmate");
      } else {
        ret = "black_won_by_mat_white";
        //console.log("Black won by checkmate");
      }
    } else if (game.isStalemate()) {
      ret = "stalemate";
    } else if (game.isThreefoldRepetition()) {
      ret = "threefold_repetition";
    } else if (game.isInsufficientMaterial()) {
      ret = "insufficient_material";
    } else if (game.isCheck()) {
      ret = "check";
    } else if (game.isDraw()) {
      ret = "draw";
    }

    return ret;
  };

  const renderCapturedPieces = (pieces, color, position) => (
  <div
    className={`captured-pieces ${position}`}
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "3px",
      padding: "6px 10px",
      borderRadius: "6px",
      backgroundColor: pieces && pieces.length > 0
        ? color === "b" ? "transparent" : "transparent"
        : "transparent",
      minHeight: "40px", // ✅ Reserve vertical space
    }}
  >
    {pieces && pieces.length > 0 ? (
      pieces.map(
        (piece, index) =>
          piece && (
            <img
              key={index}
              src={`https://res.cloudinary.com/dnlgtek2a/image/upload/v1743416894/Figures/${piece}_${color}.svg`}
              alt={`${color} ${piece}`}
              className="captured-piece"
              style={{ width: "25px", height: "25px" }}
            />
          )
      )
    ) : null}
  </div>
);


  const closeGameEnd = () => {
    setShowGameEnd(false);
    setShowAppBar(true);
  };

  const exitToMainNenu = () => {
    setShowGameEnd(false);
    navigate("/main");
  };

  const playSameGame = () => {
    setShowGameEnd(false);

    generalMessage.messageType = "new_game_request";
    navigate("/main", { state: { generalMessage } });
  };

  const rematch = () => {
    setShowGameEnd(false);

    generalMessage.messageType = "new_game_request";
    navigate("/main");
  };

  const captureBoard = async () => {
    setShowScreenshotExtras(true);

    // Wait for next paint cycle (QR + message fully rendered)
    await new Promise((resolve) => setTimeout(resolve, 100));

    if (!ref.current) {
      console.error("ref.current is null — cannot capture screenshot");
      setShowScreenshotExtras(false);
      return;
    }

    try {
      const image = await takeScreenShot(ref.current);
      if (!image) throw new Error("Screenshot failed or returned null");

      // Download the image
      const a = document.createElement("a");
      a.href = image;
      a.download = createFileName("jpg", "DemocraticChess");
      a.click();
    } catch (err) {
      console.error("Screenshot error:", err);
    } finally {
      setShowScreenshotExtras(false);
    }
  };

  const handleAccordionChange = (isExpanded) => {
    setIsAccordionOpen(isExpanded);
  };
  const handleGameHistoryChange = (isOpen) => {
    setShowGameHistory(isOpen);
  };

   const handleConfirm = async () => {
      
      setIsCoinsDialogOpen(false);
       const generalMessage = { ...generalMessageTemplate };
          generalMessage.messageType = "analitics_request";
          generalMessage.language = localStorage.getItem("i18nextLng") || "en"; 
          generalMessage.gameInfo.gameId = gameInfo.gameId;
          generalMessage.gameInfo.basicGameHistory =
            localStorage.getItem("moveHistory");
          generalMessage.gameInfo.color = gameInfo.color;
          generalMessage.gameInfo.pgn = gameInfo.pgn;
          generalMessage.gameInfo.gameTeamType = gameInfo.gameTeamType;
          sendMessage(generalMessage);
          navigate("/loader", {
            state: {
              boardWidth: 400,
              message: "Wait_for_analyzing", 
              lottieUrl:
                "https://res.cloudinary.com/dnlgtek2a/raw/upload/v1743418492/Lottie/analysis.json",
            },
          });
    };
  
    const handlePurchaseCoins = () => {
      setIsCoinsDialogOpen(false);
      alert('Redirecting to coin purchase...');
    };

  return (
    <div>
      {/* Viewer Dialog */}
      <Dialog open={showViewerDialog} onClose={handleDeclineViewer}>
        <DialogTitle>{t("Join as Viewer?")}</DialogTitle>
        <DialogContent>
          <Typography>
            {t("Would you like to join this game as a viewer?")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeclineViewer}>{t("No")}</Button>
          <Button
            onClick={handleJoinAsViewer}
            variant="contained"
            color="secondary"
          >
            {t("Yes")}
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentConfirmationDialog
              isOpen={isCoinsDialogOpen}
              onClose={() => setIsCoinsDialogOpen(false)}
              onConfirm={handleConfirm}
              onPurchaseCoins={handlePurchaseCoins}
              title={t("Confirm Feature Purchase")}
              description={t("Unlock advanced analytics and detailed move analysis for your chess games.")}
              coinsRequired={50}
              currentCoins={userData?.userStatistics?.coins || 0}
              feature={t("Premium Chess Analytics")}
            />

      {/* Emoji Dialog */}
      <Dialog open={openEmojiDialog} onClose={handleEmojiDialogClose}>
        <DialogTitle>{t("Emoji Message")}</DialogTitle>
        <DialogContent>
          <Typography>{emojiMessage}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowEmojiAgain}
                onChange={handleDontShowEmojiAgainChange}
              />
            }
            label={t("Don't show again")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmojiDialogClose}>{t("OK")}</Button>
        </DialogActions>
      </Dialog>

      {/* Draw Offer Dialog */}
      <Dialog open={openOfferDrawDialog} onClose={handleOfferDrawClose}>
        <DialogTitle>{t("Draw Offer")}</DialogTitle>
        <DialogContent>
          <Typography>{offerDrawMessage}</Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={handleDontShowAgainChange}
              />
            }
            label={t("Don't show this dialog again")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOfferDrawReject}>{t("Reject")}</Button>
          <Button
            onClick={handleOfferDrawConfirm}
            variant="contained"
            color="secondary"
          >
            {t("Accept")}
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 1, // bring chessboard above
        }}
      >
        {gameOver && showGameEnd && (
          <div style={{ marginTop: "10px", marginBottom: "20px" }}>
            <GameEndInfo
              gameInfoFromServer={gameInfo}
              onRematch={rematch}
              onPlaySameGame={playSameGame}
              onExitToMainMenu={exitToMainNenu}
              onSaveGame={() => console.log("Save Game clicked")}
              onShareGameHistory={() =>
                console.log("Share Game History clicked")
              }
              onShareBoardScreenshot={captureBoard}
              onClose={closeGameEnd}
              isViewer={isViewer}
              pgn={pgn}
              onAnalytics ={onAnalytics }
            />
          </div>
        )}

        {!gameOver && (
          <div>
            {playerColor === "black"
              ? renderCapturedPieces(capturedWhite, "b", "top")
              : renderCapturedPieces(capturedBlack, "w", "top")}
          </div>
        )}

        {!gameOver && opponentTime > 0 && (
          <div className="clock-wrapper">
            <Clock
              path="https://res.cloudinary.com/dnlgtek2a/raw/upload/v1747128561/Lottie/timer.json"
              time={opponentTime}
              width="50px"
              height="50px"
              marginRight="-5px"
              loop
              autoplay={
                !(
                  (playerColor === "white" && game.turn() === "w") ||
                  (playerColor === "black" && game.turn() === "b")
                )
              }
              avatars={
                generalMessageTeamStatus?.game[
                  playerColor === "white" ? "blackTeam" : "whiteTeam"
                ] || []
              }
              position="top"
              userData={userData}
            />
          </div>
        )}

        <div
          className={`chess-container ${gameOver ? "game-end-margin" : ""}`}
          style={isViewer ? { marginTop: "120px" } : undefined}
        >
          {showGameEnd === false && (
            <div ref={ref} className="chess-board-wrapper">
              <Chessboard
                position={fen}
                onPieceDrop={(source, target) => {
                  const moveOk = handleMove(source, target);
                  return moveOk; // return true/false to allow or disallow move
                }}
                onSquareClick={handleSquareClick}
                boardOrientation={playerColor}
                boardWidth={window.innerWidth > 600 ? 500 : 350}
                customSquareStyles={combinedSquareStyles}
              />
              {showScreenshotExtras && (
                <Box
                  sx={{
                    mt: 4, // space above the QR block
                    mb: 2, // space below the QR block
                    backgroundColor: "white",
                    borderRadius: 3,
                    padding: 2, // more padding inside
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    boxShadow: "0 6px 12px rgba(0,0,0,0.3)",
                    width: "80%", // larger width
                    maxWidth: "400px", // cap it for large screens
                    marginLeft: "10px",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="black"
                    sx={{ fontWeight: "bold" }}
                  >
                    {t("Scan to visit")}
                  </Typography>

                  <Box sx={{ backgroundColor: "white", p: 1, borderRadius: 2 }}>
                    <QRCodeSVG
                      value="https://democraticchess.com"
                      size={96} // larger QR size
                      bgColor="#ffffff"
                      fgColor="#000000"
                    />
                  </Box>

                  <Typography variant="caption" color="black">
                    https://democraticchess.com
                  </Typography>
                </Box>
              )}
            </div>
          )}

          {!isMobile && showGameHistory && (
            <div className="move-log-container">
              <div className="move-log-desktop">
                <MoveLog
                  isMobileView={false}
                  t={t}
                  getMoveHistory={getMoveHistory}
                  getCurrentMoveIndex={() => getCurrentMoveIndex}
                  setCurrentMoveIndex={setCurrentMoveIndex}
                  game={game}
                  setFen={setFen}
                  updateCapturedPieces={updateCapturedPieces}
                  disableClick={!gameOver}
                  onClose={() => handleGameHistoryChange(false)}
                />
              </div>
            </div>
          )}
        </div>

        {!gameOver && evaluation && (
          <div className="evaluation-bar-wrapper">
            <EvaluationBar
              evaluation={evaluation}
              playerColor={playerColor}
              boardWidth={window.innerWidth > 600 ? 500 : 350}
            />
          </div>
        )}

        {!gameOver && (
          <div>
            {playerColor === "black"
              ? renderCapturedPieces(capturedBlack, "w", "bottom")
              : renderCapturedPieces(capturedWhite, "b", "bottom")}
          </div>
        )}

        <div className="clock-wrapper">
          {!isAccordionOpen && isViewer === false &&(
             <Clock
              path="https://res.cloudinary.com/dnlgtek2a/raw/upload/v1747128561/Lottie/timer.json"
              time={playerTime}
              width="50px"
              height="50px"
              loop
              autoplay={
                (playerColor === "white" && game.turn() === "w") ||
                (playerColor === "black" && game.turn() === "b")
              }
              avatars={
                generalMessageTeamStatus?.game[
                  playerColor === "white" ? "whiteTeam" : "blackTeam"
                ] || []
              }
              position="bottom"
              userData={generalMessageViaState?.user}
              totalMoves={totalMoves}
              totalGuesses={totalGuesses}
              movesGuesses={movesGuesses}
              onRequestHint={handleHintClick}
              game = {generalMessageViaState}
              // for control panel
              boardWidth={
                window.innerWidth > 600 ? desktopScreen : mobileScreen
              }
              playerColorFromServer={playerColor}
              gameIdFromServer={gameId}
              onCaptureBoard={captureBoard}
              setIsViewer={isViewer}
              gameOver={gameOver}
              onAccordionChange={handleAccordionChange}
              onGameHistoryChange={handleGameHistoryChange}
              getMoveHistory={getMoveHistory}
              aiLevel={aiLevel}
              gameInfo={gameInfo}
              evaluation={evaluation}
              showGameHistory={showGameHistory}
              topSubscription={generalMessage?.user?.topSubscription}
            />
          )}
        </div>

        {isMobile && showGameHistory && (
          <div className="move-log-mobile">
            <MoveLog
              isMobileView={true}
              t={t}
              getMoveHistory={getMoveHistory}
              getCurrentMoveIndex={() => getCurrentMoveIndex}
              setCurrentMoveIndex={setCurrentMoveIndex}
              game={game}
              setFen={setFen}
              updateCapturedPieces={updateCapturedPieces}
              disableClick={!gameOver}
              onClose={() => handleGameHistoryChange(false)}
            />
          </div>
        )}
      </Box>
    </div>
  );
};

export default ResponsiveChessPage;
